/**
 * Phase 8.4 — Minimal, PII-free client analytics.
 *
 * Fires events to POST /api/analytics/events. Never blocks UX: all failures
 * are swallowed. A per-tab UUID is generated on first use and kept in
 * sessionStorage (so analytics_events never cross-reference lead records
 * unless the user explicitly submits a form that carries the same id).
 *
 * Usage:
 *   import { trackEvent } from '@/lib/analytics'
 *   trackEvent('hero_cta_click', { target: 'explore_tools' })
 *
 * Reserved event names (keep these stable — PPC dashboards key off them):
 *   - page_view
 *   - hero_cta_click
 *   - calculator_submit
 *   - calculator_result_view
 *   - email_capture_submit
 *   - apply_step_view
 *   - apply_submit
 *   - lead_magnet_impression
 *   - lead_magnet_cta_click
 *   - exit_intent_fired
 */
import { apiClient } from '@/lib/api'

const SESSION_KEY = 'lwp_analytics_session_id'

function getSessionId() {
  try {
    let id = sessionStorage.getItem(SESSION_KEY)
    if (!id) {
      id = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
      sessionStorage.setItem(SESSION_KEY, id)
    }
    return id
  } catch (_) {
    // sessionStorage unavailable (private mode, old iOS) — use a per-call id
    return `s_anon_${Math.random().toString(36).slice(2, 10)}`
  }
}

function sanitizeData(data) {
  if (!data || typeof data !== 'object') return undefined
  const out = {}
  let count = 0
  for (const [k, v] of Object.entries(data)) {
    if (count >= 20) break
    if (typeof v === 'string') out[k.slice(0, 40)] = v.slice(0, 200)
    else if (typeof v === 'number' || typeof v === 'boolean') out[k.slice(0, 40)] = v
    // ignore nested objects / arrays — keep the wire payload tiny
    count++
  }
  return Object.keys(out).length ? out : undefined
}

export function trackEvent(event, data) {
  // Never throw — marketing site must stay resilient.
  try {
    const body = {
      event: String(event).toLowerCase().replace(/[^a-z0-9_.-]/g, '_').slice(0, 64),
      session_id: getSessionId(),
      path: typeof window !== 'undefined' ? window.location.pathname : null,
      referrer: typeof document !== 'undefined' ? document.referrer || null : null,
      data: sanitizeData(data),
    }
    // Use sendBeacon when the tab is unloading; otherwise axios post.
    if (
      typeof navigator !== 'undefined' &&
      'sendBeacon' in navigator &&
      (data?._beacon === true)
    ) {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/analytics/events`
      const blob = new Blob([JSON.stringify(body)], { type: 'application/json' })
      navigator.sendBeacon(url, blob)
      return
    }
    // Fire-and-forget; no await, no error surfacing.
    apiClient.post('/analytics/events', body).catch(() => {})
  } catch (_) {
    /* ignore */
  }
}

export function trackPageView() {
  trackEvent('page_view')
}
